import { Divider, Heading } from '@sparemin/blockhead';
import { noop, over } from 'lodash-es';
import React from 'react';
import { FieldErrors } from 'react-hook-form';
import useToggle from 'hooks/useToggle/useToggle';
import { useAuth } from 'ui/context';
import useGoogleOneTapAuth from 'ui/hooks/useGoogleOneTapAuth';
import {
  AuthenticationProvider,
  AuthSuccessResponse,
  SocialAuthSuccessResponse,
  UserAuthResult,
} from 'ui/types';
import { createUserAuthResult } from 'ui/utils';
import AppleSignInButton from '../AppleSignInButton';
import AuthCard, { AuthCardProps } from '../AuthCard';
import AuthNavLink from '../AuthNavLink';
import AuthSpacer from '../AuthSpacer';
import GoogleSignInButton from '../GoogleSignInButton';
import LoginForm, { LoginFormProps } from '../LoginForm';
import { LoginFormData } from '../LoginForm/useLoginForm';
import { LOGIN_CARD_TITLE } from './constants';

export type LoginProvider = Extract<
  AuthenticationProvider,
  'google-sign-in' | 'google-one-tap' | 'headliner' | 'apple'
>;

export type LoginSubmitProvider = Exclude<LoginProvider, 'google-one-tap'>;

export interface LoginCardProps
  extends Omit<AuthCardProps, 'children' | 'footer' | 'header'>,
    Pick<LoginFormProps, 'onForgotPasswordPress'> {
  /**
   * Called when authentication completes successfully.  The card will keep all
   * inputs disabled until the promise returned by this function resolves.
   */
  onAuthenticateSuccess?: (
    provider: LoginProvider,
    result: UserAuthResult,
  ) => Promise<void>;
  /**
   * Called when there is at least one error submitting the form.
   * `errors.unregisteredError` corresponds to an error that doesn't necessarily map
   * to a form field (e.g. a general API error)
   *
   * When there is a validation error submitting the form, `errors` will be of type
   * `FieldErrors<LoginFormData>`.  When there is an error that occurs after the form
   * has been validated (for example, a failed API request), then the `errors` will
   * be of type `Error`
   */
  onError?: (errors: FieldErrors<LoginFormData> | Error) => void;
  /**
   * Called whenever a login mechanism is initiated - e.g. clicking on a social
   * auth button or submitting the credentials auth form.
   *
   * Note that Google does not expose any hook for detecting when the one-tap
   * button is clicked, so onSubmit is not called with provider 'google-one-tap'
   */
  onSubmit?: (provider: LoginSubmitProvider) => void;
}

/**
 * A card to handle login requests.
 */
export const LoginCard: React.FC<LoginCardProps> = ({
  onAuthenticateSuccess,
  onError = noop,
  onForgotPasswordPress,
  onSubmit,
}) => {
  const { features } = useAuth();
  const {
    value: loading,
    toggleOn: startLoading,
    toggleOff: stopLoading,
  } = useToggle();

  const supportsRegistration = features.includes('sign-up');

  const createAuthenticateSuccessHandler =
    (provider: LoginProvider) =>
    async (res: AuthSuccessResponse | SocialAuthSuccessResponse) => {
      try {
        // if the auth form was used, event type is "login", otherwise it is left
        // as undefined so that createUserAuthResult can determine whether or not the
        // user was created
        const authEventType = provider === 'headliner' ? 'login' : undefined;

        await onAuthenticateSuccess?.(
          provider,
          createUserAuthResult(res, authEventType),
        );
      } finally {
        stopLoading();
      }
    };

  const createSubmitHandler = (provider: LoginSubmitProvider) => () => {
    onSubmit?.(provider);
  };

  useGoogleOneTapAuth({
    onSuccess: createAuthenticateSuccessHandler('google-one-tap'),
  });

  return (
    <AuthCard
      footer={
        !supportsRegistration ? null : (
          <AuthCard.Footer>
            <Heading level={2}>
              Need a new account?{' '}
              <AuthNavLink appearance="link" to="sign-up" variant="inherit">
                Sign up here
              </AuthNavLink>
            </Heading>
          </AuthCard.Footer>
        )
      }
      header={<AuthCard.Header title={LOGIN_CARD_TITLE} />}
    >
      <AuthCard.Contents as={AuthSpacer} align="start">
        {/* TODO handle errors */}
        <AppleSignInButton
          isDisabled={loading}
          onCancel={stopLoading}
          onError={stopLoading}
          onSuccess={createAuthenticateSuccessHandler('apple')}
          onPress={over([createSubmitHandler('apple'), startLoading])}
        />
        {/* TODO handle onError, default error message */}
        <GoogleSignInButton
          isDisabled={loading}
          onCancel={stopLoading}
          onPress={over([createSubmitHandler('google-sign-in'), startLoading])}
          onError={stopLoading}
          onSuccess={createAuthenticateSuccessHandler('google-sign-in')}
        />
        <Divider space="0px">OR</Divider>
        <LoginForm
          isDisabled={loading}
          onAuthenticateSuccess={createAuthenticateSuccessHandler('headliner')}
          onError={over([onError, stopLoading])}
          onForgotPasswordPress={onForgotPasswordPress}
          onSubmit={over([createSubmitHandler('headliner'), startLoading])}
        />
      </AuthCard.Contents>
    </AuthCard>
  );
};

export default LoginCard;
